import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RegistrationStatus } from '__generated__/graphql';

import { FlexProps, UIProps } from '../../../../theme/mixins';
import RegistrationStatusLabel from '../../../Common/RegistrationStatusLabel';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

export interface StatusLabelsProps {
  amount?: number;
  remainingAmount?: number;
  refundedAmount?: number;
  balance?: number;
  cancelled?: boolean;
  waitlist?: boolean;
  preregistration?: boolean;
  draft?: boolean;
  sc?: FlexProps['flex'] & UIProps;
}

const StatusLabels = ({
  amount = 0, remainingAmount = 0, refundedAmount = 0, balance = 0, cancelled = false, waitlist = false,
  preregistration = false, draft = false, sc,
}: StatusLabelsProps) => {
  const isPending = amount > 0 && amount === remainingAmount;

  const isFullyCancelled = cancelled
    && amount + remainingAmount + refundedAmount + balance === 0
    && !waitlist
    && !preregistration;

  const hasRemainingAmount = remainingAmount > 0;
  const hasCreditedAmount = balance > 0;
  const hasRefundedAmount = refundedAmount > 0;

  return (
    <UI.FlexContainer sc={{ wrap: 'wrap', gap: 0.125, small: true, ...sc }} style={{ display: 'inline-flex' }}>
      {isFullyCancelled && (
        <CancelledLabel />
      )}

      {!isFullyCancelled && draft && (
        <DraftLabel />
      )}

      {!isFullyCancelled && !draft && (
        <>
          {(!hasRemainingAmount && !hasCreditedAmount && !hasRefundedAmount && !waitlist && !preregistration) && (
            <PaidLabel />
          )}

          {hasCreditedAmount && (
            <CreditedAmountLabel value={balance} />
          )}

          {isPending && (
            <PendingLabel />
          )}

          {!isPending && hasRemainingAmount && (
            <RemainingAmountLabel value={remainingAmount} />
          )}

          {hasRefundedAmount && (
            <RefundedLabel value={refundedAmount} />
          )}

          {waitlist && (
            <LabelContainer>
              <RegistrationStatusLabel status={RegistrationStatus.waitlist} />
            </LabelContainer>
          )}

          {preregistration && (
            <LabelContainer>
              <RegistrationStatusLabel status={RegistrationStatus.preregistration} />
            </LabelContainer>
          )}
        </>
      )}
    </UI.FlexContainer>
  );
};

const LabelContainer = styled(UI.Div)`
  ${UI.Label} {
    display: block;
    padding: 1px 5px;
    white-space: nowrap;
  }
`;

const PaidLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'primary.50', color: 'primary.900' }}>
        {t('paid')}
      </UI.Label>
    </LabelContainer>
  );
};

const CancelledLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'error.100', color: 'error.700' }}>
        {t('cancelled')}
      </UI.Label>
    </LabelContainer>
  );
};

export const DraftLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700' }}>
        {t('draft')}
      </UI.Label>
    </LabelContainer>
  );
};

export const PendingLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700' }}>
        {t('pending')}
      </UI.Label>
    </LabelContainer>
  );
};

const CreditedAmountLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'primary.50', color: 'primary.900' }}>
        {t('n_amount_credit', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

const RemainingAmountLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700' }}>
        {t('n_amount_remaining', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

const RefundedLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'secondary.100', color: 'secondary.700' }}>
        {t('n_amount_refunded', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

export default StatusLabels;

